import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BulkUploadResponseDto,
  CreateElectionDto,
  ElectionCountsResponseDto,
  ElectionResponseDto,
  PaginatedElectionResponseDto,
  PaginatedEligibleVoterResponseDto,
  PaginatedMyElectionResponseDto,
  UpdateElectionDto
} from '@thro-clients/polyas-connector-api';

import { AxiosRequestConfig } from 'axios';

import {
  createElection,
  deleteElection,
  getAdminElections,
  getAdminElectionsCounts,
  getBulkUploadJobStatus,
  getElectionById,
  getElegibleVotersForElection,
  getElegibleVotersForElectionCount,
  getElegibleVotersForElectionFilter,
  getMyElections,
  getMyElectionsCounts,
  setElectionVotingStatus,
  updateElection,
  uploadVoters
} from './polyasService';

export const useMyElections = (config?: AxiosRequestConfig) => {
  return useQuery<PaginatedMyElectionResponseDto, Error>({
    queryKey: ['myElections', config],
    queryFn: () => {
      return getMyElections(config);
    },
    initialData: undefined
  });
};

export const useMyElectionsCounts = (config?: AxiosRequestConfig) => {
  return useQuery<ElectionCountsResponseDto, Error>({
    queryKey: ['myElectionsCounts'],
    queryFn: () => {
      return getMyElectionsCounts(config);
    },
    initialData: { all: 0, future: 0, active: 0, finished: 0 }
  });
};

export const useAdminElections = (config?: AxiosRequestConfig) => {
  return useQuery<PaginatedElectionResponseDto, Error>({
    queryKey: ['adminElections', config],
    queryFn: () => {
      return getAdminElections(config);
    },
    initialData: undefined
  });
};

export const useAdminElectionsCounts = (config?: AxiosRequestConfig) => {
  return useQuery<ElectionCountsResponseDto, Error>({
    queryKey: ['adminElectionsCounts'],
    queryFn: () => {
      return getAdminElectionsCounts(config);
    },
    initialData: { all: 0, future: 0, active: 0, finished: 0 }
  });
};

export const useGetElectionById = (electionId: string, config?: AxiosRequestConfig) => {
  return useQuery<ElectionResponseDto, Error>({
    queryKey: ['election', electionId, config],
    queryFn: () => {
      return getElectionById(electionId, config);
    },
    initialData: undefined
  });
};

export const useGetElectionByIdForUpdate = (electionId?: string, config?: AxiosRequestConfig) => {
  return useQuery<ElectionResponseDto, Error>({
    queryKey: ['election', electionId, config],
    queryFn: electionId ? () => getElectionById(electionId, config) : skipToken
  });
};

export const useGetElegibleVotersForElection = (
  electionId: string,
  config?: AxiosRequestConfig
) => {
  return useQuery<PaginatedEligibleVoterResponseDto, Error>({
    queryKey: ['electionVoters', electionId],
    queryFn: () => {
      return getElegibleVotersForElection(electionId, config);
    },
    initialData: undefined
  });
};

export const useGetElegibleVotersForElectionCount = (
  electionId: string,
  config?: AxiosRequestConfig
) => {
  return useQuery<{ count: number; votedCount: number }, Error>({
    queryKey: ['electionVoters', electionId],
    queryFn: () => {
      return getElegibleVotersForElectionCount(electionId, config);
    },
    initialData: undefined
  });
};

export const useGetElegibleVotersForElectionFilter = (
  electionId: string,
  config?: AxiosRequestConfig
) => {
  return useQuery<PaginatedEligibleVoterResponseDto, Error>({
    queryKey: ['electionVoters', electionId, config],
    queryFn: () => {
      return getElegibleVotersForElectionFilter(electionId, config);
    },
    initialData: undefined
  });
};

export const useCreateElectionMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey1 = ['myElections'];
  const queryKey2 = ['adminElections'];
  const queryKey3 = ['adminElectionsCounts'];

  const create = useMutation<ElectionResponseDto, Error, { data: CreateElectionDto }>({
    mutationFn: async args => {
      return await createElection(args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKey1 });
      queryClient.invalidateQueries({ queryKey: queryKey2 });
      queryClient.invalidateQueries({ queryKey: queryKey3 });
    },
    onError: data => {}
  });

  const update = useMutation<ElectionResponseDto, Error, { id: string; data: UpdateElectionDto }>({
    mutationFn: async args => {
      return await updateElection(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: queryKey1 });
      queryClient.invalidateQueries({ queryKey: queryKey2 });
      queryClient.invalidateQueries({ queryKey: queryKey3 });
    },
    onError: data => {}
  });
  return { create, update } as const;
};

export const useMarkVotedMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['myElections'];

  const markAsVoted = useMutation<void, Error, { electionId: string }>({
    mutationFn: async args => {
      // Implement API call to mark as voted
      return await setElectionVotingStatus(args.electionId, true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const unmarkAsVoted = useMutation<void, Error, { electionId: string }>({
    mutationFn: async args => {
      return await setElectionVotingStatus(args.electionId, false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { markAsVoted, unmarkAsVoted } as const;
};

export const useBulkUploadJobStatus = (
  electionId: string,
  jobId?: string,
  config?: AxiosRequestConfig
) => {
  return useQuery<
    {
      id: string;
      status: string;
      progress: number;
      result: BulkUploadResponseDto;
      error: string;
      createdAt: Date;
    },
    Error
  >({
    queryKey: ['bulkUploadStatus', electionId, jobId],
    queryFn: () => {
      if (!jobId) {
        throw new Error('Job ID is required to fetch bulk upload job status');
      }
      return getBulkUploadJobStatus(electionId, jobId, config);
    },
    enabled: !!jobId,
    initialData: undefined
  });
};

//! IMPORTANT Reevaluate the invalidate queries
export const useUploadVoterMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey1 = ['myElections'];
  const queryKey2 = ['adminElections'];
  const queryKey3 = ['election'];
  const queryKey4 = ['electionVoters'];

  const uploadVoter = useMutation<
    { message: string; jobId: string; statusUrl: string },
    Error,
    { electionId: string; blob: Blob }
  >({
    mutationFn: async args => {
      const formData = new FormData();
      formData.append('file', args.blob);
      formData.append('requestedFormat', 'text');
      return await uploadVoters(args.electionId, formData, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey1 });
      queryClient.invalidateQueries({ queryKey: queryKey2 });
      queryClient.invalidateQueries({ queryKey: queryKey3 });
      queryClient.invalidateQueries({ queryKey: queryKey4 });
    },
    onError: data => {}
  });
  return { uploadVoter } as const;
};

export const useDeleteElectionMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey1 = ['myElections'];
  const queryKey2 = ['adminElections'];
  const queryKey3 = ['adminElectionsCounts'];

  const deleteElectionMutation = useMutation<void, Error, { id: string }>({
    mutationFn: async args => {
      return await deleteElection(args.id, config);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey1 });
      queryClient.invalidateQueries({ queryKey: queryKey2 });
      queryClient.invalidateQueries({ queryKey: queryKey3 });
    },
    onError: data => {}
  });
  return { deleteElectionMutation } as const;
};
